import React from "react";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { formatCalendarDate, formatInstant } from "../../lib/calendar-date";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { missingRateNote } from "../../lib/missing-rate-note";

/**
 * What an invoice for this order would charge now: GET
 * /surface-rentals/billing/calculate/:orderId, shown as sent.
 *
 * Each period line is the API's own rows, never a browser sum (api#958):
 *   - the days THIS invoice would bill, `billed_from` – `billed_to` (days an
 *     earlier invoice billed are not billed again), × the line's rate;
 *   - standby days, when there are any;
 *   - the mob fee and the demob fee as their own rows when this invoice
 *     carries them (`includes_mob` / `includes_demob`; billed once). A null
 *     fee is NO fee and gets no row; a $0 fee is a $0 row.
 * A refused or pending line shows the API's reason. A missing rate nulls the
 * subtotal, tax and total (api#854): they read `—`, and one line says why.
 *
 * DAMAGE CHARGES (fieldiq-api#1037, #815 G): a PRICED charge is its own line
 * (`damage_charges`, kind 'damage_charge') at the API's amount, inside the
 * API's subtotal and tax. A PENDING one has no price: it is counted in one
 * line ("1 damage charge awaiting price") and never blocks an invoice.
 *
 * HAULS (rentals-haul item 5, api#1054): when the company bills hauls by the
 * hour, each completed delivery or pickup with a truck is its own line
 * (`haul_lines`, kind 'haul'): hours × the price book's hourly haul rate, at
 * the API's amount. Missing hours or rate is `—` with the API's reason, never
 * $0, and it nulls the totals; `haul_unrated_count` says how many. A mob / demob
 * fee that also bills the trip is flagged on the line, not removed.
 */

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const money = (v: unknown) => fmtCurrency(parseMoney(v));
const day = (d: unknown) => formatCalendarDate(str(d), { month: "short", day: "numeric", year: "numeric" }) ?? "—";
const ASSETS = { one: "asset", many: "assets" };
const HAULS = { one: "haul", many: "hauls" };
const TRIP: Record<string, string> = { delivery: "Delivery", pickup: "Pickup" };

function Row({ label, sub, amount, testId }: { label: string; sub?: string | null; amount: string; testId?: string }) {
  return (
    <div data-testid={testId} className="flex justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-fieldiq-text">{label}</p>
        {sub && <p className="text-[11px] text-fieldiq-muted">{sub}</p>}
      </div>
      <span className="whitespace-nowrap tabular-nums text-fieldiq-text">{amount}</span>
    </div>
  );
}

/**
 * The period lines, as GET /billing/calculate sends them — and as a generated
 * invoice STORES them (`rental_invoices.line_items` is the same shape), so the
 * invoice page shows its lines with this too.
 */
/** "1 day", "10 days"; an absent count is "— days". */
const dayCount = (v: unknown) => {
  const n = parseMoney(v);
  return `${displayValue(n)} ${n === 1 ? "day" : "days"}`;
};

export function RentalBillingLines({ lines, empty = "No rental periods on this order" }: { lines: Record<string, unknown>[]; empty?: string }) {
  return lines.length === 0 ? (
        <p className="text-fieldiq-muted">{empty}</p>
      ) : (
        <>{lines.map((l, i) => {
          const asset = [str(l.asset_name), str(l.asset_number)].filter(Boolean).join(" · ") || "—";
          if (l.kind === "haul") {
            const hours = parseMoney(l.haul_hours);
            const rate = parseMoney(l.haul_rate);
            const overlap = Array.isArray(l.mob_fee_overlap) ? (l.mob_fee_overlap as Record<string, unknown>[]) : [];
            const trip = TRIP[String(l.dispatch_type)] ?? "Trip";
            const sub =
              parseMoney(l.amount) != null && hours != null && rate != null
                ? `${day(l.haul_day)} · ${displayValue(hours)} h × ${money(rate)} / h`
                : `${day(l.haul_day)} · ${str(l.missing_reason) ?? "Not priced"}`;
            return (
              <div key={i} data-testid="billing-line" className="space-y-1 rounded-fi-control border border-fieldiq-border p-2">
                <Row label={`Haul — ${trip}${str(l.truck_unit) ? ` · ${str(l.truck_unit)}` : ""}`} sub={sub} amount={money(l.amount)} testId="billing-line-haul" />
                {overlap.length > 0 && (
                  <p data-testid="billing-line-haul-overlap" className="text-[11px] text-fieldiq-text">
                    A mob / demob fee also bills this trip: {overlap.map((o) => str(o.asset_number) ?? "—").join(", ")}.
                  </p>
                )}
              </div>
            );
          }
          if (l.kind === "damage_charge") {
            return (
              <div key={i} data-testid="billing-line" className="space-y-1 rounded-fi-control border border-fieldiq-border p-2">
                <Row label={`${asset} — damage`} sub={str(l.description)} amount={money(l.amount)} testId="billing-line-damage" />
              </div>
            );
          }
          const reason = str(l.refused_reason) ?? str(l.billing_pending_reason);
          const rateMissing = parseMoney(l.billing_rate) === null;
          const standbyDays = parseMoney(l.standby_days);
          return (
            <div key={i} data-testid="billing-line" className="space-y-1 rounded-fi-control border border-fieldiq-border p-2">
              {reason ? (
                <Row label={asset} sub={reason} amount="—" />
              ) : (
                <Row
                  label={asset}
                  sub={
                    str(l.billed_from) == null
                      ? "No new days to bill"
                      : `${day(l.billed_from)} – ${day(l.billed_to)} · ${dayCount(l.rental_days)} × ${
                          rateMissing ? "no rate on file" : money(l.billing_rate)
                        }`
                  }
                  amount={money(l.line_subtotal)}
                  testId="billing-line-rental"
                />
              )}
              {!reason && standbyDays != null && standbyDays > 0 && (
                <Row label="Standby" sub={`${dayCount(standbyDays)} × ${money(l.standby_rate)}`} amount={money(l.standby_subtotal)} testId="billing-line-standby" />
              )}
              {l.includes_mob === true && parseMoney(l.mob_fee) != null && <Row label="Mob fee" amount={money(l.mob_fee)} testId="billing-line-mob" />}
              {l.includes_demob === true && parseMoney(l.demob_fee) != null && <Row label="Demob fee" amount={money(l.demob_fee)} testId="billing-line-demob" />}
            </div>
          );
        })}</>
      );
}

export default function RentalBillingPreview({ billing, timeZone }: { billing: Record<string, unknown>; timeZone: string | null }) {
  const lines = [
    ...(Array.isArray(billing.line_items) ? (billing.line_items as Record<string, unknown>[]) : []),
    // Priced damage charges bill as their own lines (#815 G).
    ...(Array.isArray(billing.damage_charges) ? (billing.damage_charges as Record<string, unknown>[]) : []),
    // Hauls billed by the hour (api#1054).
    ...(Array.isArray(billing.haul_lines) ? (billing.haul_lines as Record<string, unknown>[]) : []),
  ];
  const haulNote = missingRateNote(billing.haul_unrated_count, HAULS, "no hours or rate");
  const pendingCharges = parseMoney(billing.damage_charges_pending) ?? 0;
  const taxUnconfigured = billing.tax_status === "unconfigured" || !!billing.tax_unconfigured_reason;
  const unratedNote = parseMoney(billing.subtotal) === null ? missingRateNote(billing.unrated_count, ASSETS) : null;

  return (
    <div className="space-y-2 text-sm" data-testid="billing-preview-body">
      <RentalBillingLines lines={lines} />
      <div className="space-y-1 border-t border-fieldiq-border pt-2">
        <div className="flex justify-between">
          <span className="text-fieldiq-muted">Subtotal</span>
          <span data-testid="billing-subtotal" className="tabular-nums text-fieldiq-text">{money(billing.subtotal)}</span>
        </div>
        {taxUnconfigured ? (
          <p className="text-xs text-fieldiq-text">
            Tax is not configured for this order{str(billing.tax_unconfigured_reason) ? `: ${str(billing.tax_unconfigured_reason)}` : ""}.
          </p>
        ) : billing.tax_status === "exempt" ? (
          <p className="text-xs text-fieldiq-text">This customer is tax exempt.</p>
        ) : (
          [1, 2].map((n) =>
            str(billing[`tax_rate_${n}_label`]) ? (
              <div key={n} className="flex justify-between">
                <span className="text-fieldiq-muted">
                  {str(billing[`tax_rate_${n}_label`])} ({displayValue(parseMoney(billing[`tax_rate_${n}_percent`]))}%)
                </span>
                <span className="tabular-nums text-fieldiq-text">{money(billing[`tax_rate_${n}_amount`])}</span>
              </div>
            ) : null,
          )
        )}
        <div className="flex justify-between font-bold">
          <span className="text-fieldiq-text">Total</span>
          <span data-testid="billing-total" className="tabular-nums text-fieldiq-text">{money(billing.total)}</span>
        </div>
        {unratedNote && (
          <p data-testid="billing-unrated-note" className="text-[11px] text-fieldiq-text">
            Not totalled: {unratedNote}. An invoice cannot be generated until it does.
          </p>
        )}
        {haulNote && (
          <p data-testid="billing-haul-unrated" className="text-[11px] text-fieldiq-text">
            {haulNote}. An invoice can't be generated until then.
          </p>
        )}
        {pendingCharges > 0 && (
          <p data-testid="billing-damage-pending" className="text-[11px] text-fieldiq-text">
            {displayValue(pendingCharges)} damage {pendingCharges === 1 ? "charge" : "charges"} awaiting price; not on this bill.
          </p>
        )}
        <p className="text-[11px] text-fieldiq-muted">As of {formatInstant(str(billing.as_of), timeZone) ?? "—"}</p>
      </div>
    </div>
  );
}
